import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Calendar, Clock, Users, Phone, Mail, User, Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { restaurantInfo, openingHours } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { logger } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [bookingsClosed, setBookingsClosed] = useState(false);
  const [bookingsClosedMessage, setBookingsClosedMessage] = useState('');

  const guestOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '12', '15', '20'];

  // Check the master "close bookings" switch on mount
  useEffect(() => {
    axios.get(`${API}/bookings/status`).then(res => {
      setBookingsClosed(!!res.data?.closed);
      setBookingsClosedMessage(res.data?.message || '');
    }).catch(() => { /* default to open */ });
  }, []);

  // Fetch availability when date or guests change
  useEffect(() => {
    if (formData.date && formData.guests) {
      fetchAvailability(formData.date, formData.guests);
      // Reset time if it was previously selected
      setFormData(prev => ({ ...prev, time: '' }));
    } else {
      setAvailability([]);
    }
  }, [formData.date, formData.guests]);

  const fetchAvailability = async (date, guests) => {
    setLoadingAvailability(true);
    try {
      const res = await axios.get(`${API}/bookings/availability`, {
        params: { date, guests }
      });
      setAvailability(res.data);
    } catch (error) {
      logger.error('Error fetching availability:', error);
      setAvailability([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.time) {
      toast.error('Please select a time slot');
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/bookings`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: formData.date,
        time: formData.time,
        guests: formData.guests,
        special_requests: formData.specialRequests
      });

      if (response.data) {
        setConfirmedBooking(response.data);
        setIsSubmitted(true);
        toast.success('Booking confirmed!');
      }
    } catch (error) {
      logger.error('Booking error:', error);
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || 'That time slot is no longer available. Please choose another.');
        // Refresh availability
        if (formData.date && formData.guests) {
          fetchAvailability(formData.date, formData.guests);
        }
      } else {
        toast.error('Failed to submit booking. Please try again or call us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const availableSlots = availability.filter(s => s.available);
  const allFullyBooked = availability.length > 0 && availableSlots.length === 0;

  if (isSubmitted && confirmedBooking) {
    return (
      <div className="min-h-screen bg-[#FFFBF5]">
        <Header />
        <section className="pt-32 pb-24 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-3xl p-12 shadow-lg">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={40} className="text-green-600" />
              </div>
              <h1 data-testid="booking-confirmed-heading" className="font-serif text-4xl font-bold text-[#1B4332] mb-4">
                Booking Confirmed!
              </h1>
              <p className="text-[#636E72] text-lg mb-6">
                Great news, {formData.name}! Your table has been reserved.
              </p>

              <div className="bg-[#1B4332] text-white rounded-2xl p-6 mb-8 text-left max-w-sm mx-auto">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/70">Date</span>
                    <span className="font-semibold">{new Date(formData.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Time</span>
                    <span className="font-semibold">{formData.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Guests</span>
                    <span className="font-semibold">{formData.guests}</span>
                  </div>
                  {confirmedBooking.table_name && (
                    <div className="flex justify-between">
                      <span className="text-white/70">Table</span>
                      <span className="font-semibold">{confirmedBooking.table_name}</span>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-[#636E72] mb-8 text-sm">
                A confirmation email has been sent to {formData.email}. If you need to make changes, please call us.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  data-testid="make-another-booking-btn"
                  onClick={() => {
                    setIsSubmitted(false);
                    setConfirmedBooking(null);
                    setAvailability([]);
                    setFormData({
                      name: '', email: '', phone: '',
                      date: '', time: '', guests: '', specialRequests: ''
                    });
                  }}
                  className="btn-primary"
                >
                  Make Another Booking
                </Button>
                <a href={`tel:${restaurantInfo.phone}`}>
                  <Button variant="outline" className="btn-secondary w-full">
                    <Phone size={18} className="mr-2" />
                    Call Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block text-sm font-semibold tracking-wider text-[#C67C4E] uppercase mb-4">
            Reservations
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1B4332] mb-6">
            Book a Table
          </h1>
          <p className="text-base sm:text-lg text-[#636E72] max-w-2xl mx-auto">
            Reserve your table at La Pasteria Pizza Bar. Tables are automatically assigned and confirmed instantly.
          </p>
        </div>
      </section>

      {/* Bookings Closed Banner */}
      {bookingsClosed && (
        <section className="px-4 pb-8">
          <div data-testid="bookings-closed-banner" className="max-w-2xl mx-auto bg-[#1B4332] text-white rounded-2xl p-8 text-center shadow-lg">
            <AlertCircle size={36} className="mx-auto mb-3 text-[#C67C4E]" />
            <h2 className="font-serif text-2xl font-bold mb-2">Online Bookings Are Temporarily Closed</h2>
            <p className="text-white/90 mb-4">
              {bookingsClosedMessage || "We're not accepting online bookings right now. Please call us to make a reservation."}
            </p>
            <a href={`tel:${restaurantInfo.phone}`}>
              <Button className="bg-[#C67C4E] hover:bg-[#B36C40] text-white">
                <Phone size={18} className="mr-2" />
                Call {restaurantInfo.phone}
              </Button>
            </a>
          </div>
        </section>
      )}

      {/* Booking Form */}
      {!bookingsClosed && (
      <section className="pb-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-lg">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1B4332] font-medium flex items-center gap-2">
                        <User size={16} />
                        Full Name
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        data-testid="booking-name-input"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Smith"
                        className="h-12 border-[#1B4332]/20 focus:border-[#1B4332] rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1B4332] font-medium flex items-center gap-2">
                        <Phone size={16} />
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        data-testid="booking-phone-input"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="07123 456789"
                        className="h-12 border-[#1B4332]/20 focus:border-[#1B4332] rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1B4332] font-medium flex items-center gap-2">
                      <Mail size={16} />
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="booking-email-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="h-12 border-[#1B4332]/20 focus:border-[#1B4332] rounded-lg"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    {/* Date */}
                    <div className="space-y-2">
                      <Label htmlFor="date" className="text-[#1B4332] font-medium flex items-center gap-2">
                        <Calendar size={16} />
                        Date
                      </Label>
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        required
                        min={today}
                        data-testid="booking-date-input"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="h-12 border-[#1B4332]/20 focus:border-[#1B4332] rounded-lg"
                      />
                    </div>

                    {/* Guests */}
                    <div className="space-y-2">
                      <Label className="text-[#1B4332] font-medium flex items-center gap-2">
                        <Users size={16} />
                        Guests
                      </Label>
                      <Select
                        value={formData.guests}
                        onValueChange={(value) => handleSelectChange('guests', value)}
                        required
                      >
                        <SelectTrigger data-testid="booking-guests-select" className="h-12 border-[#1B4332]/20 focus:border-[#1B4332] rounded-lg">
                          <SelectValue placeholder="How many guests?" />
                        </SelectTrigger>
                        <SelectContent>
                          {guestOptions.map((num) => (
                            <SelectItem key={num} value={num}>
                              {num} {num === '1' ? 'Guest' : 'Guests'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Time Slot Selection */}
                  <div className="space-y-3">
                    <Label className="text-[#1B4332] font-medium flex items-center gap-2">
                      <Clock size={16} />
                      Select a Time
                    </Label>

                    {!formData.date || !formData.guests ? (
                      <div className="bg-[#F5F0E8] rounded-xl p-4 text-center text-[#636E72] text-sm">
                        Please select a date and number of guests to see available times
                      </div>
                    ) : loadingAvailability ? (
                      <div className="bg-[#F5F0E8] rounded-xl p-6 text-center">
                        <Loader2 size={24} className="animate-spin mx-auto text-[#C67C4E] mb-2" />
                        <p className="text-[#636E72] text-sm">Checking availability...</p>
                      </div>
                    ) : allFullyBooked ? (
                      <div data-testid="fully-booked-message" className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                        <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
                        <p className="text-red-700 font-medium">Fully booked on this date</p>
                        <p className="text-red-600 text-sm mt-1">
                          No tables available for {formData.guests} guest{formData.guests !== '1' ? 's' : ''} on this date. Please try another date or call us.
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {availability.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            disabled={!slot.available}
                            data-testid={`time-slot-${slot.time.replace(':', '')}`}
                            onClick={() => setFormData(prev => ({ ...prev, time: slot.time }))}
                            className={`py-3 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              !slot.available
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                                : formData.time === slot.time
                                  ? 'bg-[#1B4332] text-white shadow-md scale-105'
                                  : 'bg-[#F5F0E8] text-[#1B4332] hover:bg-[#1B4332]/10 border border-[#1B4332]/20'
                            }`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Special Requests */}
                  <div className="space-y-2">
                    <Label htmlFor="specialRequests" className="text-[#1B4332] font-medium">
                      Special Requests (Optional)
                    </Label>
                    <Textarea
                      id="specialRequests"
                      name="specialRequests"
                      data-testid="booking-special-requests"
                      value={formData.specialRequests}
                      onChange={handleInputChange}
                      placeholder="Any dietary requirements, allergies, or special occasions?"
                      className="min-h-[100px] border-[#1B4332]/20 focus:border-[#1B4332] rounded-lg resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-booking-btn"
                    disabled={isSubmitting || !formData.time}
                    className="btn-terracotta w-full text-lg py-6 h-auto disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="animate-spin mr-2" size={20} /> Confirming...</>
                    ) : (
                      'Confirm Booking'
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-[#F5F0E8] rounded-2xl p-6">
                <h3 className="font-serif text-xl font-bold text-[#1B4332] mb-4 flex items-center gap-2">
                  <Clock size={20} className="text-[#C67C4E]" />
                  Opening Hours
                </h3>
                <ul className="space-y-2">
                  {openingHours.map((item) => (
                    <li key={item.day} className="flex justify-between text-[#636E72]">
                      <span>{item.day}</span>
                      <span>{item.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#1B4332] rounded-2xl p-6 text-white">
                <h3 className="font-serif text-xl font-bold mb-4">
                  Prefer to Call?
                </h3>
                <p className="text-white/80 mb-4">
                  For immediate assistance or large party bookings, give us a call.
                </p>
                <a
                  href={`tel:${restaurantInfo.phone}`}
                  className="flex items-center gap-2 text-[#C67C4E] font-semibold hover:underline"
                >
                  <Phone size={18} />
                  {restaurantInfo.phone}
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-[#1B4332]/10">
                <h3 className="font-serif text-lg font-bold text-[#1B4332] mb-3">How it works</h3>
                <ol className="space-y-3 text-sm text-[#636E72]">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#1B4332] text-white text-xs flex items-center justify-center flex-shrink-0">1</span>
                    Choose your date and party size
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#1B4332] text-white text-xs flex items-center justify-center flex-shrink-0">2</span>
                    Pick from available time slots
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#1B4332] text-white text-xs flex items-center justify-center flex-shrink-0">3</span>
                    Your table is confirmed instantly
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      <Footer />
    </div>
  );
};

export default BookingPage;
